/**
 * Axis ticks for the density curve.
 *
 * The chart plots raw API values (plants/ha, kg/ha) but labels them in the user's units
 * (plantas/m², t/ha, …), so letting recharts pick the ticks rounds the *raw* value and the
 * labels come out uneven — 3 / 5,5 / 8 / 12 for a 30.000–120.000 plants/ha domain. The ticks
 * therefore have to be chosen in display space, on a round interval, and mapped back to raw
 * values for the axis. Every density/yield conversion is a plain factor, so a round display
 * interval stays round after the round trip.
 */

export type TickPlan = {
    /** Tick positions in raw (API) units, for `<XAxis ticks>` / `<YAxis ticks>`. */
    ticks: number[];
    /** Raw domain the ticks were planned against, for `<XAxis domain>` / `<YAxis domain>`. */
    domain: [number, number];
};

/** 1-2-2.5-5-10 — the intervals that read as round at any magnitude. */
const NICE_STEPS = [1, 2, 2.5, 5, 10];

/**
 * Largest round interval that still fits about `targetCount` ticks across `span`.
 * `span` and the result are both in display units.
 */
export const niceStep = (span: number, targetCount: number): number => {
    if (!Number.isFinite(span) || span <= 0 || targetCount < 1) return 0;
    const rough = span / targetCount;
    const magnitude = 10 ** Math.floor(Math.log10(rough));
    const normalized = rough / magnitude;
    return (NICE_STEPS.find((step) => normalized <= step) ?? 10) * magnitude;
};

/** Guards against a float round trip (e.g. 2.9999999996) dropping a tick off the axis. */
const EPSILON = 1e-6;

export const planTicks = ({
    min,
    max,
    toDisplay,
    fromDisplay,
    targetCount,
    extendDomain = false,
}: {
    /** Domain in raw units. */
    min: number;
    max: number;
    /** Raw → display units. Must be linear (all density/yield conversions are). */
    toDisplay: (value: number) => number;
    /** Display → raw units, i.e. the inverse of `toDisplay`. */
    fromDisplay: (value: number) => number;
    /** Roughly how many ticks to aim for; the round interval nearest that count wins. */
    targetCount: number;
    /**
     * Round the domain outwards to the interval, so the first and last tick sit exactly on
     * the axis ends. Suits a value axis; on the density axis it would leave the curves
     * stopping short of the plot edges, which is why it is opt-in.
     */
    extendDomain?: boolean;
}): TickPlan | undefined => {
    const displayMin = toDisplay(min);
    const displayMax = toDisplay(max);
    if (![displayMin, displayMax].every(Number.isFinite) || displayMax <= displayMin) return undefined;

    const step = niceStep(displayMax - displayMin, targetCount);
    if (!step) return undefined;

    const first = extendDomain ? Math.floor(displayMin / step) * step : Math.ceil(displayMin / step - EPSILON) * step;
    const last = extendDomain ? Math.ceil(displayMax / step) * step : Math.floor(displayMax / step + EPSILON) * step;

    const ticks: number[] = [];
    // Multiplying the index keeps the ticks exactly on the interval; accumulating `+= step`
    // would drift on fractional steps (2.5, 0.1, …).
    for (let index = 0; first + index * step <= last + EPSILON * step; index += 1) {
        ticks.push(fromDisplay(first + index * step));
    }
    if (ticks.length < 2) return undefined;

    return {
        ticks,
        domain: extendDomain ? [ticks[0], ticks[ticks.length - 1]] : [min, max],
    };
};
